import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { toast } from "sonner";
import { BarChart3, Trophy, DollarSign, Target } from "lucide-react";

const ContestantStatsPage = () => {
  const [contestants, setContestants] = useState([]);
  const [selectedContestant, setSelectedContestant] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContestants();
  }, []);

  useEffect(() => {
    if (selectedContestant) {
      fetchStats();
    }
  }, [selectedContestant]);

  const fetchContestants = async () => {
    try {
      const response = await axios.get(`${API}/contestants`);
      setContestants(response.data);
      if (response.data.length > 0) {
        setSelectedContestant(response.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to fetch contestants");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/contestants/${selectedContestant}/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to fetch stats");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contestant-stats-page">
      <div>
        <h1 className="font-heading font-bold text-2xl text-text-primary flex items-center gap-2">
          <BarChart3 className="h-6 w-6 text-primary" />
          Contestant Stats
        </h1>
        <p className="text-text-secondary mt-1">View performance and standings</p>
      </div>

      {/* Contestant Selector */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-text-primary">Select Contestant</label>
        <Select value={selectedContestant} onValueChange={setSelectedContestant}>
          <SelectTrigger data-testid="contestant-select">
            <SelectValue placeholder="Select a contestant" />
          </SelectTrigger>
          <SelectContent>
            {contestants.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.first_name} {c.last_name} ({c.age_group})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {stats && (
        <>
          {/* Stats Summary */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-primary/20">
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Target className="h-5 w-5 text-primary" />
                </div>
                <p className="text-2xl font-heading font-bold text-text-primary">{stats.total_events}</p>
                <p className="text-xs text-text-secondary">Events</p>
              </CardContent>
            </Card>
            <Card className="border-accent/20">
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Trophy className="h-5 w-5 text-accent" />
                </div>
                <p className="text-2xl font-heading font-bold text-text-primary">{stats.total_points}</p>
                <p className="text-xs text-text-secondary">Points</p>
              </CardContent>
            </Card>
            <Card className="border-status-success/20">
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-status-success/20 rounded-full flex items-center justify-center mx-auto mb-2">
                  <DollarSign className="h-5 w-5 text-status-success" />
                </div>
                <p className="text-2xl font-heading font-bold text-text-primary">${stats.total_winnings.toFixed(0)}</p>
                <p className="text-xs text-text-secondary">Winnings</p>
              </CardContent>
            </Card>
          </div>

          {/* Contestant Info */}
          <Card className="border-primary/20">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Contestant Info</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-text-muted">Name</p>
                  <p className="font-medium text-text-primary">
                    {stats.contestant.first_name} {stats.contestant.last_name}
                  </p>
                </div>
                <div>
                  <p className="text-text-muted">Age Group</p>
                  <p className="font-medium text-text-primary capitalize">{stats.contestant.age_group}</p>
                </div>
                <div>
                  <p className="text-text-muted">Member Status</p>
                  <p className={`font-medium ${stats.contestant.is_member ? 'text-status-success' : 'text-text-secondary'}`}>
                    {stats.contestant.is_member ? 'Active Member' : 'Not a Member'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Placements */}
          <Card className="border-primary/20">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Recent Placements</CardTitle>
            </CardHeader>
            <CardContent>
              {stats.placements.length === 0 ? (
                <div className="text-center py-8 text-text-secondary">
                  <Trophy className="h-8 w-8 mx-auto mb-2 text-text-muted" />
                  No placements recorded yet
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-text-primary">Event</TableHead>
                      <TableHead className="text-text-primary">Play Day</TableHead>
                      <TableHead className="text-text-primary text-right">Place</TableHead>
                      <TableHead className="text-text-primary text-right">Time</TableHead>
                      <TableHead className="text-text-primary text-right">Points</TableHead>
                      <TableHead className="text-text-primary text-right">Payout</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.placements.map((p, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{p.event_name}</TableCell>
                        <TableCell className="text-text-secondary text-sm">
                          {p.playday_name || p.playday_date || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            p.place <= 3 ? 'bg-accent/20 text-accent' : 'bg-gray-100 text-text-secondary'
                          }`}>
                            {p.place}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono">{p.total?.toFixed(3)}s</TableCell>
                        <TableCell className="text-right font-bold text-accent">{p.points || '-'}</TableCell>
                        <TableCell className="text-right font-bold text-status-success">
                          {p.payout ? `$${p.payout.toFixed(2)}` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default ContestantStatsPage;
